import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Folder, ChevronDown, ChevronUp } from 'react-feather';
import { AlignJustify } from 'react-feather';
import { QuantAnalysesPanelItem } from '@/screens/Project.QuantAnalysis/AnalysesList.Item';
import { FolderContextMenu } from '@/screens/Project.QuantAnalysis/Folder.ContextMenu';

type SearchResult = {
  folderId: string;
  item: { id: number; name: string };
  isMatch: boolean;
};

type SearchFolderItemProps = {
  folderId: string;
  folderName: string;
  items: SearchResult[];
  shouldExpand: boolean;
  onItemSelect: (id: number) => void;
  selectedItemId?: number;
  searchValue: string;
};

export const SearchFolderItem = ({
  folderId,
  folderName,
  items,
  shouldExpand,
  onItemSelect,
  selectedItemId,
  searchValue,
}: SearchFolderItemProps) => {
  const [isOpen, setIsOpen] = useState(false);

  // Auto-expand folder when it matches search results
  useEffect(() => {
    if (shouldExpand) {
      setIsOpen(true);
    }
  }, [shouldExpand]);

  const toggleFolder = () => {
    setIsOpen(!isOpen);
  };

  const handleChevronClick = (e: React.MouseEvent) => {
    toggleFolder();
  };

  const handleItemSelect = (itemId: number) => () => {
    onItemSelect(itemId);
  };

  return (
    <div>
      <FolderHeader>
        <FolderContent>
          <StyledAlignJustify size={16} />
          <StyledFolder size={16} />
          <FolderName>{folderName + ` (${items.length})`}</FolderName>
          <ChevronButton onClick={handleChevronClick}>
            {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </ChevronButton>
          <FolderContextMenu folderId={Number(folderId)} folderName={folderName} />
        </FolderContent>
      </FolderHeader>
      {isOpen && (
        <FolderItems>
          {items
            .filter(({ isMatch }) => !searchValue || isMatch) // Only show matching items when searching
            .map(({ item }, index, filteredItems) => (
              <ItemWrapper key={item.id} isLast={index === filteredItems.length - 1}>
                <QuantAnalysesPanelItem
                  name={item.name}
                  selected={item.id === selectedItemId}
                  onSelect={handleItemSelect(item.id)} />
              </ItemWrapper>
            ))}
        </FolderItems>
      )}
    </div>
  );
};

const FolderHeader = styled.div(() => ({
  borderBottom: '1px solid var(--gray-l)',
  backgroundColor: 'var(--pri-01)',
  color: 'var(--black-l)',
  '&:hover': {
    backgroundColor: 'var(--gray-xl)',
  },
}));

const FolderContent = styled.div({
  display: 'flex',
  alignItems: 'center',
  padding: '15px',
});

const FolderName = styled.div({
  flex: 1,
  fontFamily: 'var(--font-semibold)',
});

const FolderItems = styled.div({
  paddingLeft: '12px',
});

const ItemWrapper = styled.div<{ isLast: boolean }>(() => ({
  borderLeft: '4px solid var(--gray-l)',
  marginBottom: '2px',
}));

const StyledAlignJustify = styled(AlignJustify)({
  marginRight: '8px',
});

const StyledFolder = styled(Folder)({
  marginRight: '8px',
});

const ChevronButton = styled('button')({
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  padding: '4px',
  marginRight: '8px',
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: 'var(--gray-l)',
  },
});
